import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { api, API, CATEGORIES } from "@/lib/api";
import AssetForm from "@/components/AssetForm";
import VoiceRecorder from "@/components/VoiceRecorder";
import PhotoExtractor from "@/components/PhotoExtractor";
import BulkImporter from "@/components/BulkImporter";
import SpreadsheetImport from "@/components/SpreadsheetImport";
import LeoPhotoAssist from "@/components/LeoPhotoAssist";
import { Microphone, Camera, FileDoc, FileXls, Pencil, CaretLeft, DeviceMobile, ArrowRight, Brain } from "@phosphor-icons/react";
import HelpLink from "@/components/HelpLink";

const TABS = [
  { id: "manual", label: "Manual Entry", icon: Pencil },
  { id: "leo", label: "Ask Leo", icon: Brain },
  { id: "voice", label: "Voice Narration", icon: Microphone },
  { id: "photo", label: "Photo OCR", icon: Camera },
  { id: "bulk", label: "Bulk Document", icon: FileDoc },
  { id: "spreadsheet", label: "Spreadsheet", icon: FileXls },
];

export default function AddAsset() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState("manual");
  // Pre-set the category if the link carried one (e.g. /assets/new?category=home_component
  // from the Operational Vault page).
  const presetCategory = searchParams.get("category");
  const [draft, setDraft] = useState(
    presetCategory ? { category: presetCategory } : null,
  );

  const handleDraft = (parsed) => {
    setDraft({ ...parsed });
    setTab("manual");
    toast.success("Draft populated — review and save below.");
  };

  const onSave = async (payload) => {
    try {
      const res = await api.post("/assets", payload);
      toast.success("Asset recorded to ledger.");
      navigate(`/assets/${res.data.id}`);
    } catch (e) {
      toast.error("Failed to save. " + (e?.response?.data?.detail || e.message));
    }
  };

  return (
    <div data-testid="add-asset-page" className="space-y-8">
      <button
        onClick={() => navigate(-1)}
        data-testid="back-btn"
        className="inline-flex items-center gap-2 text-sm text-[#5C5F58] hover:text-[#0A192F]"
      >
        <CaretLeft size={14} /> Back
      </button>

      <header className="border-b border-[#C9C4B8] pb-6">
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mb-2 flex items-center gap-3">
          <span>Ingest Engine · New Entry</span>
          <HelpLink anchor="six-doors" label="The six ingest doors" />
        </div>
        <h1 className="font-serif-display text-5xl text-[#0A192F] leading-none">Record an asset.</h1>
        <p className="text-sm text-[#5C5F58] mt-3 max-w-2xl">
          Four doors. Each leads to the same ledger. Speak, photograph, import a document, or type — the system will normalise every path into the same canonical record.
        </p>
      </header>

      {/* Walkthrough CTA — prominent, mobile-first */}
      <Link
        to="/walkthrough"
        data-testid="walkthrough-cta"
        className="group flex items-center justify-between gap-4 bg-[#0A192F] text-[#F9F8F6] border border-[#0A192F] px-6 py-5 hover:bg-[#0F2818] transition-colors"
      >
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 bg-[#1A3622] border border-[#0F2818] flex items-center justify-center shrink-0">
            <DeviceMobile size={22} weight="regular" color="#F9F8F6" />
          </div>
          <div>
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#E0DDD6]/70">
              Mobile · Walk-the-room mode
            </div>
            <div className="font-serif-display text-2xl leading-tight mt-0.5">
              Record an entire room in one session.
            </div>
            <div className="text-xs text-[#E0DDD6]/70 mt-1">
              Pick a room → narrate or photograph each item → ledger builds itself.
            </div>
          </div>
        </div>
        <ArrowRight size={18} weight="bold" className="shrink-0 group-hover:translate-x-1 transition-transform" />
      </Link>

      {/* Tabs */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-0 border border-[#E0DDD6] bg-white" data-testid="ingest-tabs">
        {TABS.map((t, i) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`px-5 py-4 flex items-center gap-3 text-sm transition-colors border-r border-b border-[#E0DDD6] ${
                active
                  ? "bg-[#1A3622] text-[#F9F8F6]"
                  : "text-[#5C5F58] hover:text-[#0A192F] hover:bg-[#F9F8F6]"
              } ${i === TABS.length - 1 ? "border-r-0" : ""}`}
            >
              <Icon size={16} weight={active ? "fill" : "regular"} />
              <span className="font-medium">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Panel */}
      <div className="bg-white border border-[#E0DDD6] p-6 md:p-8">
        {tab === "manual" && (
          <AssetForm initial={draft} onSubmit={onSave} submitLabel="Commit to Ledger" />
        )}
        {tab === "leo" && <LeoPhotoAssist onDraftReady={handleDraft} />}
        {tab === "voice" && <VoiceRecorder onExtracted={handleDraft} />}
        {tab === "photo" && <PhotoExtractor onExtracted={handleDraft} />}
        {tab === "bulk" && <BulkImporter onDone={() => navigate("/assets")} />}
        {tab === "spreadsheet" && <SpreadsheetImport onDone={() => navigate("/assets")} />}
      </div>
    </div>
  );
}
